import React, { useEffect, useState } from "react";
import { readCache, writeCache } from "../dataCache";
import SenderDrawer from "./SenderDrawer";
import TemplateBuilder from "./TemplateBuilder";
import MobileScrollArea from "./MobileScrollArea";

const API_URL = window.location.origin;

const TABS = [
  "Domains",
  "Senders",
  "Templates",
  "Company Information",
  "Unsubscribe",
  "Timezone"
];

const TIMEZONES = [
  "Asia/Karachi",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Europe/London",
  "Europe/Berlin",
  "America/New_York",
  "America/Chicago",
  "America/Los_Angeles",
  "Australia/Sydney",
  "UTC"
];

function Copyable({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="sx-copy-btn"
      title="Copy"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
        } catch (e) {
          const ta = document.createElement("textarea");
          ta.value = text;
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 1400);
      }}
    >
      {copied ? "✓" : "⧉"}
    </button>
  );
}

/* ============================ DOMAINS TAB ============================ */
function DomainsTab() {
  const [domains, setDomains] = useState(() => readCache("domains", []));
  const [newDomain, setNewDomain] = useState("");
  const [adding, setAdding] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [verifying, setVerifying] = useState(null);
  const [error, setError] = useState("");
  const [settings, setSettings] = useState(() => readCache("accountSettings", {}));
  const [customDomain, setCustomDomain] = useState("");

  async function loadDomains() {
    try {
      const res = await fetch(`${API_URL}/api/domains`);
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setDomains(list);
      writeCache("domains", list);
    } catch (e) {
      /* keep cache */
    }
  }

  async function loadSettings() {
    try {
      const res = await fetch(`${API_URL}/api/settings`);
      const data = await res.json();
      setSettings(data || {});
      writeCache("accountSettings", data || {});
      setCustomDomain((data && data.customDomain) || "");
    } catch (e) {
      /* ignore */
    }
  }

  useEffect(() => {
    loadDomains();
    loadSettings();
  }, []);

  async function addDomain(e) {
    e.preventDefault();
    setError("");
    if (!newDomain.trim()) return;
    setAdding(true);
    try {
      const res = await fetch(`${API_URL}/api/domains`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domain: newDomain.trim() })
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || "Could not add domain.");
      } else {
        setNewDomain("");
        await loadDomains();
        setExpanded(data.id);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setAdding(false);
    }
  }

  async function verifyDomain(id) {
    setVerifying(id);
    try {
      const res = await fetch(`${API_URL}/api/domains/${id}/verify`, { method: "POST" });
      const data = await res.json();
      if (res.ok) {
        setDomains((prev) => prev.map((d) => (d.id === id ? data : d)));
        writeCache("domains", domains.map((d) => (d.id === id ? data : d)));
      }
    } catch (e) {
      /* ignore */
    } finally {
      setVerifying(null);
    }
  }

  async function deleteDomain(id) {
    if (!window.confirm("Remove this sending domain?")) return;
    try {
      await fetch(`${API_URL}/api/domains/${id}`, { method: "DELETE" });
      setDomains((prev) => prev.filter((d) => d.id !== id));
    } catch (e) {
      /* ignore */
    }
  }

  async function saveCustomDomain() {
    try {
      const res = await fetch(`${API_URL}/api/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customDomain })
      });
      const data = await res.json();
      setSettings(data);
      writeCache("accountSettings", data);
      alert("Branded custom domain saved.");
    } catch (e) {
      alert("Could not save: " + e.message);
    }
  }

  function statusFlag(d, label) {
    if (label === "DKIM") return d.dkimVerified;
    if (label === "SPF") return d.spfVerified;
    if (label === "RETURN PATH") return d.returnPathVerified;
    if (label === "TRACK") return d.trackVerified;
    if (label === "DMARC") return d.dmarcVerified;
    return false;
  }

  const subdomain = (settings && settings.brandedSubdomain) || "yourbrand.pageshub.io";

  return (
    <div className="sx-set-pane sx-domain-page">
      <div className="sx-set-section">
        <h3 className="sx-set-h3">Sending Domains</h3>
        <p className="sx-set-sub">
          Authenticate your domain to improve deliverability and protect your sender reputation. Add
          your domain below and we&#39;ll generate the DNS records (DKIM, SPF, Return Path, Track, and DMARC)
          you need to add at your DNS host (e.g. Hostinger).
        </p>
        <form className="sx-domain-add" onSubmit={addDomain}>
          <input
            type="text"
            placeholder="e.g. email.example.com"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
          />
          <button type="submit" className="sx-yellow-btn" disabled={adding}>
            {adding ? "Adding…" : "+ Add Sending Domain"}
          </button>
        </form>
        {error ? <div className="sx-set-error">{error}</div> : null}
      </div>

      <div className="sx-set-section">
        <div className="sx-set-row-head">
          <h3 className="sx-set-h3">Your Domains</h3>
        </div>
        <div className="sx-domain-table">
          <div className="sx-domain-thead">
            <span>DOMAIN</span>
            <span>DNS RECORDS</span>
            <span />
          </div>
          {domains.length === 0 ? (
            <div className="sx-domain-empty">No domains yet. Add your first sending domain above.</div>
          ) : (
            domains.map((d) => (
              <div className="sx-domain-group" key={d.id}>
                <div
                  className="sx-domain-row"
                  onClick={() => setExpanded(expanded === d.id ? null : d.id)}
                >
                  <span className="sx-domain-name">
                    <span className={`sx-domain-caret ${expanded === d.id ? "open" : ""}`}>›</span>
                    {d.domain}
                  </span>
                  <span className="sx-domain-badges">
                    {["DKIM", "SPF", "RETURN PATH", "TRACK", "DMARC"].map((label) => (
                      <span
                        key={label}
                        className={`sx-dns-badge ${statusFlag(d, label) ? "ok" : "bad"}`}
                      >
                        {label}
                      </span>
                    ))}
                  </span>
                  <span className="sx-domain-actions">
                    <button
                      type="button"
                      className="sx-domain-del"
                      title="Remove domain"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteDomain(d.id);
                      }}
                    >
                      ✕
                    </button>
                  </span>
                </div>
                {expanded === d.id ? (
                  <div className="sx-domain-detail">
                    {d.dmarcStatus === "failed" && !d.dmarcVerified ? (
                      <div className="sx-dmarc-alert">
                        <strong>✕ DMARC Verification Failed</strong>
                        <span>
                          DMARC verification failed. Please ensure you have a valid DMARC record
                          configured for your domain.
                        </span>
                      </div>
                    ) : null}
                    {d.dmarcVerified ? (
                      <div className="sx-dmarc-ok">DMARC is configured and verified.</div>
                    ) : null}
                    <div className="sx-dns-records">
                      <div className="sx-dns-head">
                        <span>HOST</span>
                        <span>TYPE</span>
                        <span>VALUE</span>
                      </div>
                      {(d.records || []).map((r) => (
                        <div className="sx-dns-rec" key={r.host}>
                          <span className="sx-dns-host">
                            <span className="sx-dns-cell-text">{r.host}</span>
                            <Copyable text={r.host} />
                          </span>
                          <span className="sx-dns-type">{r.type}</span>
                          <span className="sx-dns-value">
                            <span className="sx-dns-cell-text">{r.value}</span>
                            <Copyable text={r.value} />
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="sx-dns-foot">
                      <button
                        type="button"
                        className="sx-admin-btn sx-admin-btn-primary"
                        onClick={() => verifyDomain(d.id)}
                        disabled={verifying === d.id}
                      >
                        {verifying === d.id ? "Verifying…" : "Verify DNS Records"}
                      </button>
                      {d.lastCheckedAt ? (
                        <span className="sx-dns-checked">
                          Last checked {new Date(d.lastCheckedAt).toLocaleString()}
                        </span>
                      ) : (
                        <span className="sx-dns-checked">Not verified yet</span>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            ))
          )}
        </div>
      </div>

      <details className="sx-dns-help">
        <summary>▷ Need help setting up DNS records?</summary>
        <div className="sx-dns-help-body">
          <p>
            Log in to your DNS host (for example Hostinger), open the DNS / Zone editor for your
            domain, and add each record exactly as shown above:
          </p>
          <ol>
            <li>Copy the <strong>HOST</strong> value into the record name field.</li>
            <li>Set the record <strong>TYPE</strong> (TXT or CNAME).</li>
            <li>Paste the <strong>VALUE</strong> into the content / points-to field.</li>
            <li>Save, wait for propagation (a few minutes to a few hours), then click Verify.</li>
          </ol>
        </div>
      </details>

      <div className="sx-set-section">
        <h3 className="sx-set-h3">Branded URL Settings</h3>
        <p className="sx-set-sub">
          Your branded URL is where your popups, forms, and landing pages are hosted. Use our default
          subdomain or connect your own custom domain for a professional look.
        </p>
        <div className="sx-branded-card">
          <span className="sx-branded-num">1</span>
          <div className="sx-branded-body">
            <div className="sx-branded-title">Default Subdomain</div>
            <div className="sx-branded-desc">This is your hosted URL. All your pages are live at this address by default.</div>
            <div className="sx-branded-url">
              <span>🌐 {subdomain}</span>
              <Copyable text={subdomain} />
            </div>
          </div>
        </div>
        <div className="sx-branded-card">
          <span className="sx-branded-num">2</span>
          <div className="sx-branded-body">
            <div className="sx-branded-title">Custom Domain <em>(Optional)</em></div>
            <div className="sx-branded-desc">
              Want a professional look? Connect your own domain (e.g. pages.yourcompany.com) to host
              your popups, forms, and landing pages. This is <strong>not required</strong> for sending
              emails or email authentication.
            </div>
            <div className="sx-branded-input">
              <input
                type="text"
                placeholder="pages.yourcompany.com"
                value={customDomain}
                onChange={(e) => setCustomDomain(e.target.value)}
              />
              <button type="button" className="sx-yellow-btn" onClick={saveCustomDomain}>
                + Add Custom Domain
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================ SENDERS TAB ============================ */
/* SMTP Verification Status constants */
const VSTATUS = { IDLE: "idle", CHECKING: "checking", OK: "ok", FAIL: "fail" };

function EmailAccounts() {
  const [domains, setDomains] = useState(() => readCache("domains", []));
  const [domainEmails, setDomainEmails] = useState(() => readCache("domainEmails", []));
  const EMPTY = { domain: "", localPart: "", displayName: "" };
  const [form, setForm] = useState(EMPTY);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const [domainRes, senderRes] = await Promise.all([
        fetch(`${API_URL}/api/domains`),
        fetch(`${API_URL}/api/senders/domain-emails`)
      ]);
      const domainData = domainRes.ok ? await domainRes.json() : [];
      const senderData = senderRes.ok ? await senderRes.json() : [];
      const nextDomains = Array.isArray(domainData) ? domainData : [];
      const nextSenders = Array.isArray(senderData) ? senderData : [];
      setDomains(nextDomains);
      setDomainEmails(nextSenders);
      writeCache("domains", nextDomains);
      writeCache("domainEmails", nextSenders);
      setForm((current) => ({
        ...current,
        domain: current.domain || nextDomains[0]?.domain || ""
      }));
    } catch (error) {
      setMessage("Could not load sender addresses. " + error.message);
    }
  }

  useEffect(() => { load(); }, []);

  async function createSender(e) {
    e.preventDefault();
    const localPart = form.localPart.trim().toLowerCase();
    if (!form.domain) return setMessage("Connect and verify a sending domain first.");
    if (!localPart) return setMessage("Enter the sender email name.");

    setSaving(true);
    setMessage("");
    try {
      const response = await fetch(`${API_URL}/api/senders/domain-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          domain: form.domain,
          localPart,
          displayName: form.displayName.trim()
        })
      });
      const data = await response.json();
      if (!response.ok) {
        setMessage(data.message || "Could not add sender address.");
        return;
      }
      setForm({ ...EMPTY, domain: form.domain });
      setMessage("Sender address added. It is ready in the campaign Sender dropdown.");
      await load();
    } catch (error) {
      setMessage("Network error: " + error.message);
    } finally {
      setSaving(false);
    }
  }

  async function removeSender(id) {
    try {
      await fetch(`${API_URL}/api/senders/${id}`, { method: "DELETE" });
      await load();
    } catch (error) {
      setMessage("Could not remove sender. " + error.message);
    }
  }

  const selectedDomain = domains.find((item) => item.domain === form.domain);
  const senderPreview = form.localPart && form.domain
    ? `${form.localPart.trim().toLowerCase()}@${form.domain}`
    : "";

  return (
    <div className="sx-set-section sx-domain-senders-only">
      <div className="sx-set-row-head">
        <div>
          <h3 className="sx-set-h3">Sender Addresses</h3>
          <p className="sx-set-sub">
            Connect and verify a domain once, then create campaign sender addresses here. No mailbox,
            per-address SMTP login, password, or separate SMTP entry is required.
          </p>
        </div>
      </div>

      {domains.length === 0 ? (
        <div className="sx-set-empty">
          Connect a sending domain in the Domains tab first. After DKIM verification, sender addresses can be added here.
        </div>
      ) : (
        <form className="sx-de-form" onSubmit={createSender}>
          <div className="sx-mailbox-row">
            <div className="sx-mailbox-field sx-field-wide">
              <label className="sx-mailbox-label">Sender email</label>
              <div className="sx-mailbox-localwrap">
                <input
                  className="sx-mailbox-local"
                  placeholder="news, hello, offers"
                  value={form.localPart}
                  onChange={(e) => {
                    setForm((current) => ({
                      ...current,
                      localPart: e.target.value.replace(/\s/g, "").toLowerCase()
                    }));
                    setMessage("");
                  }}
                />
                <span className="sx-mailbox-at">@</span>
                <select
                  className="sx-mailbox-domain"
                  value={form.domain}
                  onChange={(e) => {
                    setForm((current) => ({ ...current, domain: e.target.value }));
                    setMessage("");
                  }}
                >
                  {domains.map((domain) => (
                    <option key={domain._id || domain.domain} value={domain.domain}>
                      {domain.domain}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="sx-mailbox-field">
              <label className="sx-mailbox-label">Display name</label>
              <input
                className="sx-mailbox-input"
                placeholder="Alina News"
                value={form.displayName}
                onChange={(e) => setForm((current) => ({ ...current, displayName: e.target.value }))}
              />
            </div>
          </div>

          {senderPreview ? <div className="sx-de-preview">Sender: <b>{senderPreview}</b></div> : null}
          {message ? <div className="sx-de-msg">{message}</div> : null}

          <div className="sx-mailbox-actions">
            <button
              type="submit"
              className="sx-yellow-btn"
              disabled={saving || !form.localPart || !form.domain || !selectedDomain?.dkimVerified}
            >
              {saving ? "Adding..." : "+ Add Sender Address"}
            </button>
          </div>
        </form>
      )}

      {domainEmails.length > 0 ? (
        <MobileScrollArea className="sx-set-table-wrap" ariaLabel="sender addresses table">
          <table className="sx-admin-table">
            <thead>
              <tr><th>ADDRESS</th><th>DISPLAY NAME</th><th>DOMAIN STATUS</th><th></th></tr>
            </thead>
            <tbody>
              {domainEmails.map((sender) => (
                <tr key={sender._id}>
                  <td><span className="sx-mbx-email">{sender.email}</span></td>
                  <td className="sx-muted">{sender.name || "—"}</td>
                  <td><span className="sx-badge sx-badge--green">Verified domain</span></td>
                  <td>
                    <button type="button" className="sx-link-btn sx-danger" onClick={() => removeSender(sender._id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </MobileScrollArea>
      ) : null}
    </div>
  );
}

function SendersTab() {
  const [senders, setSenders] = useState(() => readCache("sendersList", []));
  const [settings, setSettings] = useState(() => readCache("accountSettings", {}));
  const [replyTo, setReplyTo] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });

  async function load() {
    try {
      const [sRes, setRes] = await Promise.all([
        fetch(`${API_URL}/api/senders`),
        fetch(`${API_URL}/api/settings`)
      ]);
      const sData = await sRes.json();
      const setData = await setRes.json();
      const list = Array.isArray(sData) ? sData : [];
      setSenders(list);
      writeCache("sendersList", list);
      setSettings(setData || {});
      setReplyTo((setData && setData.replyToEmail) || "");
    } catch (e) {
      /* keep cache */
    }
  }
  useEffect(() => {
    load();
  }, []);

  async function addSender(e) {
    e.preventDefault();
    if (!form.email.trim()) return;
    try {
      await fetch(`${API_URL}/api/senders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });
      setForm({ name: "", email: "" });
      setShowAdd(false);
      load();
    } catch (err) {
      alert(err.message);
    }
  }

  async function makeDefault(id) {
    try {
      await fetch(`${API_URL}/api/senders/${id}/default`, { method: "PATCH" });
      load();
    } catch (e) {
      /* ignore */
    }
  }

  async function saveReplyTo() {
    try {
      const res = await fetch(`${API_URL}/api/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ replyToEmail: replyTo })
      });
      const data = await res.json();
      setSettings(data);
      writeCache("accountSettings", data);
      alert("Reply-to email saved.");
    } catch (e) {
      alert(e.message);
    }
  }

  return (
    <div className="sx-set-pane sx-senders-page">
      <EmailAccounts />
      <div className="sx-set-section">
        <h3 className="sx-set-h3">Default &quot;Reply to&quot; Email</h3>
        <div className="sx-inline-save">
          <input
            type="email"
            placeholder="reply@yourcompany.com"
            value={replyTo}
            onChange={(e) => setReplyTo(e.target.value)}
          />
          <button type="button" className="sx-yellow-btn" onClick={saveReplyTo}>Save Changes</button>
        </div>
      </div>
    </div>
  );
}

/* ============================ TEMPLATES TAB ============================ */
function TemplatesTab() {
  const [emailTemplates, setEmailTemplates] = useState(() => readCache("emailTemplates", []));
  const [landing, setLanding] = useState(() => readCache("landingTemplates", []));
  const [search, setSearch] = useState("");
  const [builder, setBuilder] = useState(null);

  async function load() {
    try {
      const [eRes, lRes] = await Promise.all([
        fetch(`${API_URL}/api/templates?kind=email`),
        fetch(`${API_URL}/api/templates?kind=landing`)
      ]);
      const eData = await eRes.json();
      const lData = await lRes.json();
      setEmailTemplates(Array.isArray(eData) ? eData : []);
      setLanding(Array.isArray(lData) ? lData : []);
      writeCache("emailTemplates", Array.isArray(eData) ? eData : []);
      writeCache("landingTemplates", Array.isArray(lData) ? lData : []);
    } catch (e) {
      /* keep cache */
    }
  }
  useEffect(() => {
    load();
  }, []);

  async function addTemplate(kind) {
    const name = window.prompt(
      kind === "email" ? "Email template name" : "Landing page template name",
      kind === "email" ? "email sender Template" : "Landing Page Template"
    );
    if (!name) return;
    try {
      await fetch(`${API_URL}/api/templates`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, kind })
      });
      load();
    } catch (e) {
      alert(e.message);
    }
  }

  async function deleteTemplate(id) {
    if (!window.confirm("Delete this template?")) return;
    try {
      await fetch(`${API_URL}/api/templates/${id}`, { method: "DELETE" });
      load();
    } catch (e) {
      /* ignore */
    }
  }

  const filtered = emailTemplates.filter((t) =>
    (t.name || "").toLowerCase().includes(search.toLowerCase())
  );

  const fmt = (d) => (d ? new Date(d).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—");

  return (
    <div className="sx-set-pane sx-templates-page">
      <div className="sx-set-section">
        <div className="sx-set-row-head">
          <h3 className="sx-set-h3">Email Templates</h3>
          <div className="sx-set-head-right">
            <input
              className="sx-set-search"
              type="text"
              placeholder="Search email templates..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button type="button" className="sx-yellow-btn" onClick={() => setBuilder("email")}>
              + Add Email Template
            </button>
          </div>
        </div>
        <MobileScrollArea className="sx-set-table-wrap" ariaLabel="settings table">
          <table className="sx-admin-table">
            <thead>
              <tr>
                <th>NAME</th>
                <th>TYPE</th>
                <th>CREATED</th>
                <th>UPDATED</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="sx-set-empty">No email templates found.</td>
                </tr>
              ) : (
                filtered.map((t) => (
                  <tr key={t._id}>
                    <td className="sx-strong">{t.name}</td>
                    <td>{t.type}</td>
                    <td className="sx-muted">{fmt(t.createdAt)}</td>
                    <td className="sx-muted">{fmt(t.updatedAt)}</td>
                    <td>
                      <button type="button" className="sx-link-btn danger" onClick={() => deleteTemplate(t._id)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </MobileScrollArea>
      </div>
      <div className="sx-set-section">
        <div className="sx-set-row-head">
          <h3 className="sx-set-h3">Landing Page Templates</h3>
          <button type="button" className="sx-yellow-btn" onClick={() => setBuilder("landing")}>
            + Add Landing Page Template
          </button>
        </div>
        {landing.length === 0 ? (
          <div className="sx-landing-empty">
            <strong>No landing page template found</strong>
            <span>Once a landing page template is created, it will appear here.</span>
          </div>
        ) : (
          <MobileScrollArea className="sx-set-table-wrap" ariaLabel="settings table">
            <table className="sx-admin-table">
              <thead>
                <tr>
                  <th>NAME</th>
                  <th>CREATED</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {landing.map((t) => (
                  <tr key={t._id}>
                    <td className="sx-strong">{t.name}</td>
                    <td className="sx-muted">{fmt(t.createdAt)}</td>
                    <td>
                      <button type="button" className="sx-link-btn danger" onClick={() => deleteTemplate(t._id)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
        </MobileScrollArea>
        )}
      </div>

      {builder ? (
        <TemplateBuilder kind={builder} onClose={() => setBuilder(null)} onSaved={load} />
      ) : null}
    </div>
  );
}

/* ============================ SETTINGS FORM TABS ============================ */
function useSettings() {
  const [settings, setSettings] = useState(() => readCache("accountSettings", {}));
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/settings`);
        const data = await res.json();
        setSettings(data || {});
        writeCache("accountSettings", data || {});
      } catch (e) {
        /* keep cache */
      } finally {
        setLoaded(true);
      }
    })();
  }, []);
  async function save(patch) {
    const res = await fetch(`${API_URL}/api/settings`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch)
    });
    const data = await res.json();
    setSettings(data);
    writeCache("accountSettings", data);
    return data;
  }
  return { settings, setSettings, save, loaded };
}

function EditorTab() {
  const { settings, save } = useSettings();
  const [ed, setEd] = useState({ defaultEditor: "drag", fontFamily: "Arial", primaryColor: "#5d5fef" });
  useEffect(() => {
    if (settings && settings.editor) setEd({ ...ed, ...settings.editor });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);
  return (
    <div className="sx-set-pane">
      <div className="sx-set-section">
        <h3 className="sx-set-h3">Editor Defaults</h3>
        <p className="sx-set-sub">Choose the default editor and styling applied to new campaigns.</p>
        <label className="sx-field">
          <span>Default Editor</span>
          <select value={ed.defaultEditor} onChange={(e) => setEd({ ...ed, defaultEditor: e.target.value })}>
            <option value="drag">Drag &amp; Drop</option>
            <option value="rich">Rich Text</option>
            <option value="html">HTML / Code</option>
          </select>
        </label>
        <label className="sx-field">
          <span>Default Font</span>
          <select value={ed.fontFamily} onChange={(e) => setEd({ ...ed, fontFamily: e.target.value })}>
            {["Arial", "Helvetica", "Georgia", "Times New Roman", "Verdana", "Roboto"].map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        </label>
        <label className="sx-field">
          <span>Primary Color</span>
          <input type="color" value={ed.primaryColor} onChange={(e) => setEd({ ...ed, primaryColor: e.target.value })} />
        </label>
        <button type="button" className="sx-yellow-btn" onClick={async () => { await save({ editor: ed }); alert("Editor settings saved."); }}>
          Save Changes
        </button>
      </div>
    </div>
  );
}

function CompanyTab() {
  const { settings, save } = useSettings();
  const [c, setC] = useState({ name: "", website: "", address: "", city: "", state: "", zip: "", country: "", phone: "" });
  useEffect(() => {
    if (settings && settings.company) setC((prev) => ({ ...prev, ...settings.company }));
  }, [settings]);
  const field = (key, label, ph) => (
    <label className="sx-field">
      <span>{label}</span>
      <input type="text" placeholder={ph || label} value={c[key] || ""} onChange={(e) => setC({ ...c, [key]: e.target.value })} />
    </label>
  );
  return (
    <div className="sx-set-pane">
      <div className="sx-set-section">
        <h3 className="sx-set-h3">Company Information</h3>
        <p className="sx-set-sub">This information is used in your email footer to comply with anti-spam laws (CAN-SPAM).</p>
        <div className="sx-field-grid">
          {field("name", "Company Name")}
          {field("website", "Website")}
          {field("address", "Street Address")}
          {field("city", "City")}
          {field("state", "State / Province")}
          {field("zip", "ZIP / Postal Code")}
          {field("country", "Country")}
          {field("phone", "Phone")}
        </div>
        <button type="button" className="sx-yellow-btn" onClick={async () => { await save({ company: c }); alert("Company information saved."); }}>
          Save Changes
        </button>
      </div>
    </div>
  );
}

function UnsubscribeTab() {
  const { settings, save } = useSettings();
  const [u, setU] = useState({ headerText: "", buttonText: "Unsubscribe", redirectUrl: "" });
  useEffect(() => {
    if (settings && settings.unsubscribe) setU((prev) => ({ ...prev, ...settings.unsubscribe }));
  }, [settings]);
  return (
    <div className="sx-set-pane">
      <div className="sx-set-section">
        <h3 className="sx-set-h3">Unsubscribe Page</h3>
        <p className="sx-set-sub">Customise what recipients see when they unsubscribe from your emails.</p>
        <label className="sx-field">
          <span>Header Text</span>
          <textarea rows={2} value={u.headerText} onChange={(e) => setU({ ...u, headerText: e.target.value })} />
        </label>
        <label className="sx-field">
          <span>Button Text</span>
          <input type="text" value={u.buttonText} onChange={(e) => setU({ ...u, buttonText: e.target.value })} />
        </label>
        <label className="sx-field">
          <span>Redirect URL (after unsubscribe)</span>
          <input type="text" placeholder="https://yourcompany.com/bye" value={u.redirectUrl} onChange={(e) => setU({ ...u, redirectUrl: e.target.value })} />
        </label>
        <button type="button" className="sx-yellow-btn" onClick={async () => { await save({ unsubscribe: u }); alert("Unsubscribe settings saved."); }}>
          Save Changes
        </button>
      </div>
    </div>
  );
}

function TimezoneTab() {
  const { settings, save } = useSettings();
  const [tz, setTz] = useState("Asia/Karachi");
  useEffect(() => {
    if (settings && settings.timezone) setTz(settings.timezone);
  }, [settings]);
  return (
    <div className="sx-set-pane">
      <div className="sx-set-section">
        <h3 className="sx-set-h3">Timezone</h3>
        <p className="sx-set-sub">Campaign scheduling and reports use this timezone.</p>
        <label className="sx-field">
          <span>Account Timezone</span>
          <select value={tz} onChange={(e) => setTz(e.target.value)}>
            {TIMEZONES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </label>
        <button type="button" className="sx-yellow-btn" onClick={async () => { await save({ timezone: tz }); alert("Timezone saved."); }}>
          Save Changes
        </button>
      </div>
    </div>
  );
}

/* ============================ MAIN ============================ */
export default function SettingsDomains() {
  const [tab, setTab] = useState("Domains");
  return (
    <div className="sx-settings-page">
      <div className="sx-settings-tabs">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            className={`sx-settings-tab ${tab === t ? "active" : ""}`}
            onClick={() => setTab(t)}
          >
            {t}
          </button>
        ))}
      </div>
      {tab === "Domains" ? <DomainsTab /> : null}
      {tab === "Senders" ? <SendersTab /> : null}
      {tab === "Templates" ? <TemplatesTab /> : null}
      {tab === "Company Information" ? <CompanyTab /> : null}
      {tab === "Unsubscribe" ? <UnsubscribeTab /> : null}
      {tab === "Timezone" ? <TimezoneTab /> : null}
    </div>
  );
}
